import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  HelpCircle, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Plus,
  MessageSquare,
  Send,
  Calendar,
  User,
  AlertCircle,
  FileText,
  X
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import { partnerApiService } from '@/services/partnerApiService'

interface Inquiry {
  id: number
  title: string
  content: string
  status: string
  category?: string
  priority: string
  admin_reply?: string
  replied_at?: string
  created_at: string
  updated_at: string
  unread_count: number
}

interface InquiryCategory {
  value: string
  label: string
}

const PartnerInquiries = () => {
  const [inquiries, setInquiries] = useState<Inquiry[]>([])
  const [categories, setCategories] = useState<InquiryCategory[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // 새 문의 작성 폼 상태
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newInquiry, setNewInquiry] = useState({
    title: '',
    content: '',
    category: '',
    priority: 'NORMAL'
  })

  // 페이지네이션
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')

  const { t, i18n } = useTranslation('inquiries')
  const locale = i18n.language === 'ko' ? 'ko-KR' : i18n.language === 'ja' ? 'ja-JP' : i18n.language === 'zh' ? 'zh-CN' : 'en-US'

  useEffect(() => {
    loadInquiries()
    loadCategories()
  }, [currentPage, statusFilter])

  const loadInquiries = async () => {
    try {
      setLoading(true)
      console.log('Loading inquiries with partnerApiService', { currentPage, statusFilter })
      
      const data = await partnerApiService.getInquiries(currentPage, 20, statusFilter)
      
      if (data.result === 'success') {
        setInquiries(data.data || [])
        setTotalPages(Math.ceil((data.total || 0) / 20))
      } else {
        throw new Error(data.message || t('loadError'))
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('loadError'))
      console.error('문의 목록 로드 오류:', err)
      toast.error(t('loadError'))
    } finally {
      setLoading(false)
    }
  }

  const loadCategories = async () => {
    try {
      console.log('Loading categories with partnerApiService')
      
      const data = await partnerApiService.getInquiryCategories()
      
      if (data.result === 'success') {
        setCategories(data.data || [])
      } else {
        throw new Error(data.message || t('categoryLoadError'))
      }
    } catch (err) {
      console.error('카테고리 로드 오류:', err)
      // 카테고리 로드 실패시 기본 카테고리 사용
      const defaultCategories = [
        { value: 'settlement', label: t('categories.settlement') },
        { value: 'technical', label: t('categories.technical') },
        { value: 'account', label: t('categories.account') },
        { value: 'marketing', label: t('categories.marketing') },
        { value: 'other', label: t('categories.other') }
      ]
      setCategories(defaultCategories)
    }
  }

  const handleCreateInquiry = async () => {
    if (!newInquiry.title.trim() || !newInquiry.content.trim()) {
      toast.error(t('form.validationError'))
      return
    }

    try {
      console.log('Creating inquiry with partnerApiService', newInquiry)
      
      const data = await partnerApiService.createInquiry({
        title: newInquiry.title.trim(),
        content: newInquiry.content.trim(),
        category: newInquiry.category || undefined,
        priority: newInquiry.priority
      })
      
      if (data.result === 'success') {
        toast.success(t('form.success'))
        setShowCreateForm(false)
        setNewInquiry({ title: '', content: '', category: '', priority: 'NORMAL' })
        loadInquiries() // 목록 새로고침
      } else {
        throw new Error(data.message || t('form.error'))
      }
    } catch (err) {
      console.error('문의 생성 오류:', err)
      toast.error(err instanceof Error ? err.message : t('form.error'))
    }
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      PENDING: { color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', text: t('status.pending'), icon: Clock },
      IN_PROGRESS: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', text: t('status.inProgress'), icon: AlertCircle },
      ANSWERED: { color: 'bg-green-500/20 text-green-400 border-green-500/30', text: t('status.answered'), icon: CheckCircle },
      CLOSED: { color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', text: t('status.closed'), icon: XCircle }
    }
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.PENDING
    const Icon = config.icon
    
    return (
      <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-lg text-xs font-medium border ${config.color}`}>
        <Icon className="w-3 h-3" />
        <span>{config.text}</span>
      </div>
    )
  }

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      LOW: { color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', text: t('priority.low') },
      NORMAL: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', text: t('priority.normal') },
      HIGH: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', text: t('priority.high') },
      URGENT: { color: 'bg-red-500/20 text-red-400 border-red-500/30', text: t('priority.urgent') }
    }
    
    const config = priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.NORMAL
    
    return (
      <div className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium border ${config.color}`}>
        {config.text}
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner w-8 h-8" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="glass-card p-6 border-l-4 border-red-500">
          <div className="flex items-center space-x-2 text-red-400">
            <XCircle className="w-5 h-5" />
            <span className="font-medium">{error}</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <Helmet>
        <title>{t('pageTitle')}</title>
      </Helmet>

      {/* 헤더 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">{t('title')}</h1>
          <p className="text-sm sm:text-base text-dark-400 mt-1">{t('subtitle')}</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="glass-button flex items-center justify-center space-x-2 px-3 py-2 sm:px-4 sm:py-2 bg-primary-500/20 border-primary-500/30 hover:bg-primary-500/30 text-primary-400 w-full sm:w-auto text-sm sm:text-base"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">{t('newInquiry')}</span>
          <span className="sm:hidden">{t('newInquiryMobile')}</span>
        </button>
      </motion.div>

      {/* 필터 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="glass-card p-3 sm:p-4"
      >
        <div className="flex flex-wrap gap-3 sm:gap-4">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value)
              setCurrentPage(1)
            }}
            className="glass-input px-3 py-2 rounded-lg text-sm bg-dark-800/50 border border-white/10 text-white min-w-[120px] flex-1 sm:flex-none"
          >
            <option value="">{t('filter.allStatus')}</option>
            <option value="PENDING">{t('filter.pending')}</option>
            <option value="IN_PROGRESS">{t('filter.inProgress')}</option>
            <option value="ANSWERED">{t('filter.answered')}</option>
            <option value="CLOSED">{t('filter.closed')}</option>
          </select>
        </div>
      </motion.div>

      {/* 문의 목록 */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="glass-card"
      >
        {inquiries.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-dark-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="w-8 h-8 text-dark-500" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">{t('noInquiries')}</h3>
            <p className="text-dark-400 mb-6">{t('noInquiriesDescription')}</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="glass-button px-4 py-2 sm:px-6 sm:py-3 bg-primary-500/20 border-primary-500/30 hover:bg-primary-500/30 text-primary-400 text-sm sm:text-base"
            >
              {t('writeInquiry')}
            </button>
          </div>
        ) : (
          <div className="divide-y divide-white/10">
            {inquiries.map((inquiry) => (
              <motion.div
                key={inquiry.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="p-3 sm:p-6 hover:bg-white/5 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col gap-2 sm:gap-3 mb-3">
                      <h3 className="text-base sm:text-lg font-medium text-white pr-2 break-words">{inquiry.title}</h3>
                      <div className="flex flex-wrap gap-2">
                        {getStatusBadge(inquiry.status)}
                        {getPriorityBadge(inquiry.priority)}
                      </div>
                    </div>
                    
                    <p className="text-sm sm:text-base text-dark-300 mb-3 sm:mb-4 line-clamp-2 sm:line-clamp-3 break-words">{inquiry.content}</p>
                    
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4 text-xs sm:text-sm text-dark-400">
                      <div className="flex items-center space-x-1">
                        <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span className="truncate">{t('list.createdAt', { date: new Date(inquiry.created_at).toLocaleDateString(locale) })}</span>
                      </div>
                      {inquiry.category && (
                        <div className="flex items-center space-x-1">
                          <FileText className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                          <span className="truncate">{t('list.category', { category: inquiry.category })}</span>
                        </div>
                      )}
                      {inquiry.admin_reply && (
                        <div className="flex items-center space-x-1 text-green-400">
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                          <span>{t('list.hasReply')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {inquiry.admin_reply && (
                  <div className="mt-3 sm:mt-4 glass-card bg-green-500/10 border-green-500/20 p-3 sm:p-4">
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <User className="w-3 h-3 sm:w-4 sm:h-4 text-green-400 flex-shrink-0" />
                        <span className="text-xs sm:text-sm font-medium text-green-400">{t('list.adminReply')}</span>
                      </div>
                      {inquiry.replied_at && (
                        <span className="text-xs text-green-500">
                          {new Date(inquiry.replied_at).toLocaleString(locale)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-green-300 break-words">{inquiry.admin_reply}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>

      {/* 새 문의 작성 모달 */}
      <AnimatePresence>
        {showCreateForm && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="glass-card w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center p-4 sm:p-6 border-b border-white/10">
                <h2 className="text-lg sm:text-xl font-bold text-white">{t('form.title')}</h2>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="glass-button p-2 rounded-lg hover:bg-red-500/20"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                {/* 제목 */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    {t('form.titleLabel')}
                  </label>
                  <input
                    type="text"
                    value={newInquiry.title}
                    onChange={(e) => setNewInquiry({ ...newInquiry, title: e.target.value })}
                    className="glass-input w-full px-3 py-2 rounded-lg bg-dark-800/50 border border-white/10 text-white placeholder-dark-400"
                    placeholder={t('form.titlePlaceholder')}
                  />
                </div>

                {/* 카테고리 */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    {t('form.categoryLabel')}
                  </label>
                  <select
                    value={newInquiry.category}
                    onChange={(e) => setNewInquiry({ ...newInquiry, category: e.target.value })}
                    className="glass-input w-full px-3 py-2 rounded-lg bg-dark-800/50 border border-white/10 text-white"
                  >
                    <option value="">{t('form.categoryPlaceholder')}</option>
                    {categories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 우선순위 */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    {t('form.priorityLabel')}
                  </label>
                  <select
                    value={newInquiry.priority}
                    onChange={(e) => setNewInquiry({ ...newInquiry, priority: e.target.value })}
                    className="glass-input w-full px-3 py-2 rounded-lg bg-dark-800/50 border border-white/10 text-white"
                  >
                    <option value="LOW">{t('priority.low')}</option>
                    <option value="NORMAL">{t('priority.normal')}</option>
                    <option value="HIGH">{t('priority.high')}</option>
                    <option value="URGENT">{t('priority.urgent')}</option>
                  </select>
                </div>

                {/* 내용 */}
                <div>
                  <label className="block text-sm font-medium text-white mb-2">
                    {t('form.contentLabel')}
                  </label>
                  <textarea
                    value={newInquiry.content}
                    onChange={(e) => setNewInquiry({ ...newInquiry, content: e.target.value })}
                    rows={6}
                    className="glass-input w-full px-3 py-2 rounded-lg bg-dark-800/50 border border-white/10 text-white placeholder-dark-400 resize-none"
                    placeholder={t('form.contentPlaceholder')}
                  />
                </div>
              </div>

              {/* 버튼 */}
              <div className="flex gap-3 p-4 sm:p-6 border-t border-white/10">
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 glass-button py-3 bg-gray-500/20 border-gray-500/30 hover:bg-gray-500/30 text-gray-300"
                >
                  {t('common:cancel')}
                </button>
                <button
                  onClick={handleCreateInquiry}
                  className="flex-1 glass-button py-3 bg-primary-500/20 border-primary-500/30 hover:bg-primary-500/30 text-primary-400 flex items-center justify-center space-x-2"
                >
                  <Send className="w-4 h-4" />
                  <span>{t('form.submit')}</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex justify-center"
        >
          <div className="flex items-center gap-2 text-sm sm:text-base">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="glass-button px-2 py-1 sm:px-3 sm:py-2 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
            >
              {t('common:previous')}
            </button>
            
            <span className="px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm text-dark-400 whitespace-nowrap">
              {currentPage} / {totalPages}
            </span>
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="glass-button px-2 py-1 sm:px-3 sm:py-2 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
            >
              {t('common:next')}
            </button>
          </div>
        </motion.div>
      )}
    </div>
  )
}

export default PartnerInquiries